import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, Droplets, Zap, Plus, FileText, Bell, AlertTriangle, Trash2, ShieldCheck, Wifi } from "lucide-react";
import UtilitiesDashboardStats from "./_utilitiesdashboardstats";
import UtilitiesFilterDashboard from "./_utilitiesfilterdashboard";
import RecentMeterReadings from "./_recentmeterreadings";
import { useGetLandlordPropertyQuery, useGetUtilitiesSummaryDataQuery } from "../../lib/apiSlice/property/propertySlice.tsx";
import AddMeterReadingModal from "./_addmeterreading.jsx";
import ReminderModal from "../dashboard/_reminderModal.jsx";
import { serverUrl } from "../../../../serverUrl.js";
import jwt_decode from "jwt-decode";
import { toast } from 'react-toastify';
import { fetchLandlordIds } from '../../../../serverUrl.js';
import { useSearchParams } from "react-router-dom";


export function UtilitiesDashboard() {
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [, setFilters] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();


  // Get current filter values from URL or use defaults
  const currentMonth = searchParams.get('month') || new Date().getMonth() + 1;
  const currentYear = searchParams.get('year') || new Date().getFullYear();
  const currentApartmentIds = searchParams.get('apartment_ids') || '';

  const { data, isLoading, error: queryError, refetch } = useGetUtilitiesSummaryDataQuery({
    month: currentMonth,
    year: currentYear,
    apartment_ids: currentApartmentIds
  });
  const { data: propertiesData } = useGetLandlordPropertyQuery();


  const [showAddModal, setShowAddModal] = useState(false);
  const [uninvoicedReadings, setUninvoicedReadings] = useState([]);
  const [selectedReadingIds, setSelectedReadingIds] = useState([]);
  const [isLoadingUninvoiced, setIsLoadingUninvoiced] = useState(false);
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [isEditUtilityModalOpen, setIsEditUtilityModalOpen] = useState(false);
  const [isSavingUtility, setIsSavingUtility] = useState(false);
  const [isExpensesLoading, setIsExpensesLoading] = useState(false);
  const [expensesError, setExpensesError] = useState(null);
  const [expensesByApartment, setExpensesByApartment] = useState([]);
  const [editUtilityPayload, setEditUtilityPayload] = useState({
    expenseId: null,
    apartmentId: null,
    title: "",
    frequency: "",
    cost_per_unit: "",
    expense_type: ""
  });
  const handleReminderModalOpen = () => {
    setIsReminderModalOpen(true);
  };

  const [landlordId, setLandlordId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    fetchLandlordIds()
      .then((id) => {
        if (isMounted) {
          setLandlordId(id);
        }
      })
      .catch((fetchError) => {
        console.error("Error fetching Landlord ID:", fetchError);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSaveReading = (data) => {
    console.log("Received reading data:", data);
    // You could POST this to your API here
  };

  const handleSendReminders = (reminderData) => {
    // reminderData: { customSubject, customMessage, addTemplate, reminderValue }
    const { customSubject, customMessage, addTemplate, reminderValue } = reminderData || {};
    try {
      const accessToken = localStorage.getItem("authTokens");
      // get the access token
      const parsedTokens = JSON.parse(accessToken);
      const access = parsedTokens.access;
      // headers access token
      const config = {
        headers: { Authorization: `Bearer ${access}` },
      };
      // decoding the token so that i can get the user id
      const decodedToken = jwt_decode(access);
      const userId = decodedToken.user_id;

      // get the landlord details that are linked to the user
      serverUrl
        .get(`/landlord/landlord/?user_id=${userId}`, config)
        .then((res) => {
          // get the landlord Id
          const landlordIds = res.data.results.map((landlord) => landlord.id);

          const data = {
            message: customMessage,
            subject: customSubject,
          };
          if (addTemplate) {
            const dataTemplate = {
              title: customSubject,
              text: customMessage,
              created_by_message_template: userId,
              updated_by_message_template: userId
            }
            // post the template
            serverUrl.post(`/communication/message-templates/`, dataTemplate, config)
              .then((res) => {
                toast.success("Message template saved successfully!");
              })
              .catch((error) => {
                console.log(error)
              })
          }
          if (reminderValue === 'general') {
            serverUrl.post(`/landlord/send_payment_reminder/?landlord_id=${landlordIds}`, data, config)
              .then((res) => {
                toast.success("Reminder sent successfully to all your tenants!");
                setIsReminderModalOpen(false);
              })
              .catch((error) => {
                console.log(error)
              })
          }
          else if (reminderValue) {
            serverUrl.post(`/landlord/send_payment_reminder/?apartment_id=${reminderValue}`, data, config)
              .then((res) => {
                toast.success("Reminder sent successfully to all the tenants in the selected property!");
                setIsReminderModalOpen(false);
              })
              .catch((error) => {
                console.log(error)
              })
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) { }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    const params = new URLSearchParams();

    // Always set month and year
    params.set('month', newFilters.month);
    params.set('year', newFilters.year);

    // Handle apartment filter
    if (newFilters.apartment && newFilters.apartment !== "all") {
      params.set('apartment_ids', newFilters.apartment);
    } else {
      params.delete('apartment_ids');
    }

    setSearchParams(params);
  };

  const selectedApartmentIds = useMemo(() => {
    if (!currentApartmentIds) {
      return [];
    }
    return currentApartmentIds
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }, [currentApartmentIds]);

  const apartmentsForExpenses = useMemo(() => {
    const propertyResults = propertiesData?.results ?? propertiesData ?? [];
    const dashboardApartments = data?.landlord_expenses ?? [];
    const source = propertyResults.length ? propertyResults : dashboardApartments;

    return source
      .map((apartment) => ({
        id: apartment.id ?? apartment.apartment_id,
        name: apartment.name ?? apartment.apartment_name
      }))
      .filter((apartment) => Boolean(apartment.id));
  }, [propertiesData, data?.landlord_expenses]);

  const filteredApartmentsForInvoicing = useMemo(() => (
    selectedApartmentIds.length
      ? apartmentsForExpenses.filter((apartment) =>
          selectedApartmentIds.includes(String(apartment.id))
        )
      : apartmentsForExpenses
  ), [apartmentsForExpenses, selectedApartmentIds]);

  const loadUninvoicedReadings = useCallback(async () => {
    if (!filteredApartmentsForInvoicing.length) {
      setUninvoicedReadings([]);
      return;
    }
    const accessToken = localStorage.getItem("authTokens");
    if (!accessToken) return;
    const access = JSON.parse(accessToken).access;
    const config = { headers: { Authorization: `Bearer ${access}` } };

    setIsLoadingUninvoiced(true);
    try {
      const selectedMonthName = new Date(
        Number(currentYear),
        Number(currentMonth) - 1,
        1
      ).toLocaleString("default", { month: "long" }).toLowerCase();
      const responses = await Promise.all(
        filteredApartmentsForInvoicing.map((apartment) =>
          serverUrl.get(
            `/landlord/variable-expense-units-consumed/uninvoiced/?apartment_id=${apartment.id}&month=${selectedMonthName}&year=${currentYear}`,
            config
          )
        )
      );
      const readings = responses.flatMap((response) => response.data?.results || []);
      setUninvoicedReadings(readings);
      setSelectedReadingIds((current) =>
        current.filter((readingId) => readings.some((reading) => reading.id === readingId))
      );
    } catch (loadError) {
      console.error(loadError);
      toast.error("Unable to load readings awaiting invoicing.");
    } finally {
      setIsLoadingUninvoiced(false);
    }
  }, [filteredApartmentsForInvoicing, currentMonth, currentYear]);

  useEffect(() => {
    loadUninvoicedReadings();
  }, [loadUninvoicedReadings]);

  const toggleReadingSelection = (readingId) => {
    setSelectedReadingIds((current) =>
      current.includes(readingId)
        ? current.filter((id) => id !== readingId)
        : [...current, readingId]
    );
  };

  const generateVariableUtilityInvoices = async () => {
    const readingIds = selectedReadingIds.length
      ? selectedReadingIds
      : uninvoicedReadings.map((reading) => reading.id);
    if (!readingIds.length) {
      toast.info("There are no variable utility readings awaiting invoicing.");
      return;
    }
    const accessToken = localStorage.getItem("authTokens");
    if (!accessToken) return;
    const access = JSON.parse(accessToken).access;
    const config = { headers: { Authorization: `Bearer ${access}` } };

    setIsGeneratingInvoices(true);
    try {
      const response = await serverUrl.post(
        "/landlord/variable-expense-units-consumed/invoice/",
        { reading_ids: readingIds },
        config
      );
      toast.success(`${response.data.created_count} utility invoice(s) generated.`);
      if (response.data.skipped_count) {
        toast.warning(`${response.data.skipped_count} reading(s) skipped.`);
      }
      setSelectedReadingIds([]);
      await loadUninvoicedReadings();
      refetch();
    } catch (invoiceError) {
      console.error(invoiceError);
      toast.error(invoiceError.response?.data?.error || "Unable to generate utility invoices.");
    } finally {
      setIsGeneratingInvoices(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const apartments = selectedApartmentIds.length
      ? apartmentsForExpenses.filter((apartment) => selectedApartmentIds.includes(String(apartment.id)))
      : apartmentsForExpenses;

    if (apartments.length === 0) {
      setExpensesByApartment([]);
      setIsExpensesLoading(false);
      return undefined;
    }

    const accessToken = localStorage.getItem("authTokens");
    if (!accessToken) {
      setExpensesByApartment([]);
      setIsExpensesLoading(false);
      return undefined;
    }

    const parsedTokens = JSON.parse(accessToken);
    const access = parsedTokens.access;
    const config = {
      headers: { Authorization: `Bearer ${access}` }
    };

    setIsExpensesLoading(true);
    setExpensesError(null);

    Promise.all(
      apartments.map(async (apartment) => {
        try {
          const response = await serverUrl.get(`/landlord/expense/?apartment_id=${apartment.id}`, config);
          const expenses = response.data?.results ?? response.data ?? [];
          return {
            apartmentId: apartment.id,
            apartmentName: apartment.name,
            expenses
          };
        } catch (fetchError) {
          return {
            apartmentId: apartment.id,
            apartmentName: apartment.name,
            expenses: []
          };
        }
      })
    )
      .then((results) => {
        if (!isMounted) {
          return;
        }
        setExpensesByApartment(results);
      })
      .catch((fetchError) => {
        if (!isMounted) {
          return;
        }
        setExpensesError(fetchError);
      })
      .finally(() => {
        if (!isMounted) {
          return;
        }
        setIsExpensesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [apartmentsForExpenses, selectedApartmentIds]);

  const utilitiesFromExpensesModel = useMemo(() => {
    if (!expensesByApartment.length) {
      return [];
    }

    return expensesByApartment.flatMap((apartment) => {
      const expenses = Array.isArray(apartment.expenses) ? apartment.expenses : [];
      return expenses.map((expense) => ({
        apartmentId: apartment.apartmentId,
        apartmentName: apartment.apartmentName,
        expenseId: expense.id ?? expense.expense_id,
        title: expense.title ?? expense.expense_name ?? "",
        frequency: expense.frequency ?? "",
        cost_per_unit: expense.cost_per_unit ?? "",
        expense_type: expense.expense_type ?? expense.paymentType ?? expense.payment_type ?? ""
      }));
    });
  }, [expensesByApartment]);

  const utilitiesFallback = useMemo(() => {
    if (!data?.landlord_expenses?.length) {
      return [];
    }

    const filteredApartments = selectedApartmentIds.length
      ? data.landlord_expenses.filter((apartment) =>
          selectedApartmentIds.includes(String(apartment.apartment_id))
        )
      : data.landlord_expenses;

    return filteredApartments.flatMap((apartment) => {
      const expenses = Array.isArray(apartment.expenses) ? apartment.expenses : [];
      return expenses.map((expense) => ({
        apartmentId: apartment.apartment_id,
        apartmentName: apartment.apartment_name,
        expenseId: expense.expense_id ?? expense.id,
        title: expense.expense_name ?? expense.title ?? "",
        frequency: expense.frequency ?? "",
        cost_per_unit: expense.cost_per_unit ?? "",
        expense_type: expense.expense_type ?? expense.payment_type ?? ""
      }));
    });
  }, [data?.landlord_expenses, selectedApartmentIds]);

  const utilitiesList = useMemo(() => {
    if (utilitiesFromExpensesModel.length > 0) {
      return utilitiesFromExpensesModel;
    }
    return utilitiesFallback;
  }, [utilitiesFromExpensesModel, utilitiesFallback]);

  const handleEditUtilityOpen = (utility) => {
    setEditUtilityPayload({
      expenseId: utility.expenseId ?? null,
      apartmentId: utility.apartmentId ?? null,
      title: utility.title ?? "",
      frequency: utility.frequency ? String(utility.frequency).toLowerCase() : "",
      cost_per_unit: utility.cost_per_unit ?? "",
      expense_type: utility.expense_type ? String(utility.expense_type).toLowerCase() : ""
    });
    setIsEditUtilityModalOpen(true);
  };

  const handleEditUtilityChange = (event) => {
    const { name, value } = event.target;
    setEditUtilityPayload((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditUtilitySave = async () => {
    if (!editUtilityPayload.expenseId) {
      toast.error("Missing utility ID. Please refresh and try again.");
      return;
    }
    if (!editUtilityPayload.apartmentId) {
      toast.error("Missing apartment ID. Please refresh and try again.");
      return;
    }
    try {
      setIsSavingUtility(true);
      const accessToken = localStorage.getItem("authTokens");
      if (!accessToken) {
        toast.error("Missing access token. Please log in again.");
        return;
      }
      const parsedTokens = JSON.parse(accessToken);
      const access = parsedTokens.access;
      const config = {
        headers: { Authorization: `Bearer ${access}` }
      };

      const payload = {
        title: editUtilityPayload.title,
        frequency: editUtilityPayload.frequency ? editUtilityPayload.frequency.toLowerCase() : "",
        cost_per_unit: editUtilityPayload.cost_per_unit,
        expense_type: editUtilityPayload.expense_type ? editUtilityPayload.expense_type.toLowerCase() : "",
        apartment: parseInt(editUtilityPayload.apartmentId, 10)
      };

      await serverUrl.put(`/landlord/expense/${editUtilityPayload.expenseId}/`, payload, config);
      toast.success("Utility updated successfully!");
      setIsEditUtilityModalOpen(false);
      refetch();
    } catch (saveError) {
      console.error(saveError);
      toast.error("Failed to update utility. Please try again.");
    } finally {
      setIsSavingUtility(false);
    }
  };

  const utilityIcon = (utilityName) => {
    const normalized = utilityName?.toLowerCase() ?? "";
    if (normalized.includes("water")) {
      return <Droplets className="w-4 h-4 text-blue-500" />;
    }
    if (normalized.includes("electric")) {
      return <Zap className="w-4 h-4 text-yellow-500" />;
    }
    if (normalized.includes("garbage") || normalized.includes("waste")) {
      return <Trash2 className="w-4 h-4 text-gray-500" />;
    }
    if (normalized.includes("security")) {
      return <ShieldCheck className="w-4 h-4 text-green-500" />;
    }
    if (normalized.includes("internet") || normalized.includes("wifi")) {
      return <Wifi className="w-4 h-4 text-indigo-500" />;
    }
    return <FileText className="w-4 h-4 text-gray-400" />;
  };

  const utilitiesLoading = isLoading || isExpensesLoading;

  return (
    <div className="gap-6 grid">
      {/* Filters Section */}
      <UtilitiesFilterDashboard
        onFiltersDashboardChange={handleFiltersChange}
        onUtilityChanged={refetch}
      />

      {queryError && (
        <div className="bg-red-50 p-4 border border-red-200 rounded-lg text-red-700">
          Utility dashboard data could not be loaded. Please retry.
        </div>
      )}


      {/* Stats Cards */}
      <UtilitiesDashboardStats stats={data?.landlord_total_metrics} loading={isLoading} />

      {/* Main Content Area */}

      {/* Main Content Area */}
      <div className="gap-6 grid grid-cols-1 md:grid-cols-12 mt-3">

        {/* Quick Actions */}
        <div className="md:col-span-6 lg:col-span-5 bg-white shadow-xl p-6 rounded-lg">
          <h2 className="mb-2 font-semibold text-lg">Quick Actions</h2>
          <p className="mb-4 text-gray-500 text-sm">Common utility management tasks</p>

          <div className="space-y-3">
            <button onClick={() => setShowAddModal(true)} className="flex justify-between items-center bg-white hover:bg-blue-50 px-4 py-2 border border-blue-200 rounded-md w-full text-blue-600 transition">
              <div className="flex items-center">
                <Plus className="mr-2 w-4 h-4" />
                Add Meter Reading
              </div>
              <ArrowRight className="w-4 h-4" />
            </button>

            <button onClick={handleReminderModalOpen}
              className="flex justify-between items-center bg-white hover:bg-blue-50 px-4 py-2 border border-blue-200 rounded-md w-full text-blue-600 transition">
              <div className="flex items-center">
                <Bell className="mr-2 w-4 h-4" />
                Send Notifications
              </div>
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => document.getElementById("variable-utility-invoicing")?.scrollIntoView({ behavior: "smooth" })}
              className="flex justify-between items-center bg-white hover:bg-blue-50 px-4 py-2 border border-blue-200 rounded-md w-full text-blue-600 transition"
            >
              <div className="flex items-center">
                <FileText className="mr-2 w-4 h-4" />
                Invoice Meter Readings
                {uninvoicedReadings.length > 0 && (
                  <span className="bg-amber-100 ml-2 px-2 py-0.5 rounded-full text-amber-700 text-xs">
                    {uninvoicedReadings.length}
                  </span>
                )}
              </div>
              <ArrowRight className="w-4 h-4" />
            </button>
          
          </div>
        </div>

        {/* Alerts & Notifications */}
        <div className="md:col-span-6 lg:col-span-7 bg-white shadow-xl p-6 rounded-lg">
          <h2 className="mb-2 font-semibold text-lg">Alerts & Notifications</h2>
          <p className="mb-4 text-gray-500 text-sm">Recent system alerts</p>

          <div className="space-y-3 max-h-80 overflow-y-auto">
            {isLoading ? (
              // Skeleton loading state
              <>
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-gray-50 p-3 border border-gray-200 rounded-lg animate-pulse">
                    <div className="bg-gray-300 mt-0.5 rounded-full w-5 h-5"></div>
                    <div className="flex-1 space-y-2">
                      <div className="bg-gray-300 rounded w-3/4 h-4"></div>
                      <div className="bg-gray-300 rounded w-full h-3"></div>
                      <div className="bg-gray-300 rounded w-1/2 h-3"></div>
                    </div>
                  </div>
                ))}
                <div className="bg-gray-200 rounded w-full h-8 animate-pulse"></div>
              </>
            ) : data?.expense_notifications?.length > 0 ? (
              <>
                {data.expense_notifications.map((notification, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 bg-red-50 p-3 border border-red-200 rounded-lg"
                  >
                    <AlertTriangle className="mt-0.5 w-5 h-5 text-red-500" />
                    <div>
                      <p className="font-medium text-sm">Overdue Payment - {notification.house}</p>
                      <p className="text-gray-500 text-xs">{notification.apartment} - {notification.message}</p>
                      <p className="mt-1 text-gray-400 text-xs">Old unpaid invoice</p>
                    </div>
                  </div>
                ))}
                {/* <button
                  onClick={() => setIsAlertDialogOpen(true)}
                  className="hover:bg-blue-50 py-1 rounded w-full text-blue-600 hover:text-blue-700 text-sm transition"
                >
                  View All Alerts
                </button> */}
              </>
            ) : (
              <p className="text-gray-400 text-sm italic">No alerts to display</p>
            )}
          </div>
        </div>



      </div>

      <div className="bg-white shadow-xl p-6 rounded-lg">
        <div className="flex md:flex-row flex-col justify-between md:items-center gap-4 mb-4">
          <div>
            <h2 className="font-semibold text-lg">Utilities by Property</h2>
            <p className="text-gray-500 text-sm">
              Utilities already added.
            </p>
            {expensesError && (
              <p className="mt-2 text-red-500 text-xs">Unable to load utilities right now. Please refresh.</p>
            )}
          </div>
        </div>

        {utilitiesLoading ? (
          <div className="gap-4 grid sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, index) => (
              <div
                key={`utility-card-skeleton-${index}`}
                className="relative bg-white border border-gray-100 shadow-sm px-4 pb-4 pt-9 rounded-2xl text-center animate-pulse"
              >
                <div className="left-1/2 -top-5 absolute bg-gray-200 shadow-md rounded-xl w-9 h-9 -translate-x-1/2"></div>
                <div className="bg-gray-200 mx-auto mt-3 rounded w-3/5 h-3"></div>
                <div className="bg-gray-200 mx-auto mt-2 rounded w-4/5 h-3"></div>
                <div className="bg-gray-200 mx-auto mt-4 rounded-full w-10 h-10"></div>
              </div>
            ))}
          </div>
        ) : utilitiesList.length === 0 ? (
          <div className="border border-dashed border-gray-200 py-12 rounded-2xl text-center text-gray-500 text-sm">
            No utilities found for the selected filters.
          </div>
        ) : (
          <div className="gap-4 grid sm:grid-cols-2 lg:grid-cols-4">
            {utilitiesList.map((utility, index) => (
              <div
                key={`${utility.apartmentId}-${utility.expenseId ?? index}`}
                className="relative bg-white border border-gray-100 shadow-sm px-4 pb-4 pt-9 rounded-2xl text-center"
              >
                <div className="left-1/2 -top-5 absolute flex justify-center items-center bg-white shadow-md rounded-xl w-9 h-9 -translate-x-1/2 ring-1 ring-gray-100">
                  {utilityIcon(utility.title)}
                </div>
                <p className="text-gray-400 text-xs uppercase tracking-wide">{utility.apartmentName}</p>
                <h3 className="mt-1 font-semibold text-gray-800 text-sm">{utility.title || "Utility"}</h3>
                <div className="flex justify-center gap-2 mt-2">
                  {utility.expense_type && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                        utility.expense_type.toLowerCase() === "varying"
                          ? "bg-amber-100 text-amber-700"
                          : utility.expense_type.toLowerCase() === "fixed"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {utility.expense_type.toLowerCase() === "varying"
                        ? "Varying"
                        : utility.expense_type.toLowerCase() === "fixed"
                        ? "Fixed"
                        : utility.expense_type}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-gray-500 text-xs">
                  {utility.cost_per_unit ? `KES ${utility.cost_per_unit} per unit` : "Rate not set"}
                  {utility.frequency ? ` | ${utility.frequency}` : ""}
                </p>
                <button
                  onClick={() => handleEditUtilityOpen(utility)}
                  disabled={!utility.expenseId}
                  className="inline-flex justify-center items-center bg-gray-100 hover:bg-blue-50 mt-4 rounded-full px-4 py-2 text-blue-600 text-xs font-medium transition disabled:opacity-50"
                >
                  Edit
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div id="variable-utility-invoicing" className="bg-white shadow-xl p-6 rounded-lg">
        <div className="flex md:flex-row flex-col justify-between md:items-center gap-4 mb-4">
          <div>
            <h2 className="font-semibold text-lg">Variable utility invoicing</h2>
            <p className="text-gray-500 text-sm">
              Meter readings entered for the selected period that do not have invoices.
            </p>
          </div>
          <button
            onClick={generateVariableUtilityInvoices}
            disabled={!uninvoicedReadings.length || isGeneratingInvoices}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 px-4 py-2 rounded-md text-white"
          >
            {isGeneratingInvoices
              ? "Generating..."
              : `Generate ${selectedReadingIds.length || uninvoicedReadings.length} invoice(s)`}
          </button>
        </div>

        {isLoadingUninvoiced ? (
          <p className="py-6 text-gray-500 text-center">Loading uninvoiced readings…</p>
        ) : uninvoicedReadings.length === 0 ? (
          <div className="border border-dashed border-gray-200 py-8 rounded-lg text-gray-500 text-sm text-center">
            All entered variable utility readings have been invoiced.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="p-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedReadingIds.length === uninvoicedReadings.length}
                      onChange={(event) => setSelectedReadingIds(
                        event.target.checked ? uninvoicedReadings.map((reading) => reading.id) : []
                      )}
                    />
                  </th>
                  <th className="p-3 text-left">Property</th>
                  <th className="p-3 text-left">Unit</th>
                  <th className="p-3 text-left">Utility</th>
                  <th className="p-3 text-left">Period</th>
                  <th className="p-3 text-right">Units</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {uninvoicedReadings.map((reading) => (
                  <tr key={reading.id}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedReadingIds.includes(reading.id)}
                        onChange={() => toggleReadingSelection(reading.id)}
                      />
                    </td>
                    <td className="p-3">{reading.apartment_name}</td>
                    <td className="p-3">{reading.house_number}</td>
                    <td className="p-3">{reading.expense_title}</td>
                    <td className="p-3 capitalize">{reading.month} {reading.year}</td>
                    <td className="p-3 text-right">{reading.units_consumed}</td>
                    <td className="p-3 text-right">KES {Number(reading.amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bottom Section */}
      {/* <div className="hidden gap-6 grid grid-cols-1 md:grid-cols-7"> */}
      <div className="">
        <RecentMeterReadings recentReadings={data?.recent_meter_readings} apartments={data?.landlord_expenses} />

        {/* Recent Meter Readings */}
        {/* <RecentMeterReadings recentReadings={data?.recent_meter_readings} apartments={data?.landlord_expenses} /> */}

        {/* Right Sidebar Bottom */}
        {/* <PaymentStatusChart /> */}
      </div>


      <AddMeterReadingModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        apartments={data?.landlord_expenses}
        onSubmit={handleSaveReading}
        onSaved={() => {
          loadUninvoicedReadings();
          refetch();
        }}
      />


      <ReminderModal
        isOpen={isReminderModalOpen}
        onClose={() => setIsReminderModalOpen(false)}
        onSend={handleSendReminders}
        reminderType='landlord'
        landlord={landlordId}

      />

      {isEditUtilityModalOpen && (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black/50 p-4">
          <div className="bg-white shadow-xl rounded-lg w-full max-w-md">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-900 text-lg">Edit Utility</h3>
              <button
                onClick={() => setIsEditUtilityModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                x
              </button>
            </div>
            <div className="space-y-4 p-6">
              <div>
                <p className="block mb-1 font-extrabold text-gray-700 text-sm">Utility Name</p>
                <input
                  type="text"
                  name="title"
                  className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                  value={editUtilityPayload.title}
                  onChange={handleEditUtilityChange}
                />
              </div>
              <div>
                <p className="block mb-1 font-extrabold text-gray-700 text-sm">Payment Frequency</p>
                <select
                  name="frequency"
                  className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                  value={editUtilityPayload.frequency}
                  onChange={handleEditUtilityChange}
                >
                  <option value="">Select frequency</option>
                  <option value="monthly">Monthly</option>
                  <option value="weekly">Weekly</option>
                  <option value="daily">Daily</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
              <div>
                <p className="block mb-1 font-extrabold text-gray-700 text-sm">Cost Per Unit (KES)</p>
                <input
                  type="number"
                  name="cost_per_unit"
                  className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                  value={editUtilityPayload.cost_per_unit}
                  onChange={handleEditUtilityChange}
                />
              </div>
              <div>
                <p className="block mb-1 font-extrabold text-gray-700 text-sm">Payment Structure</p>
                <select
                  name="expense_type"
                  className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                  value={editUtilityPayload.expense_type}
                  onChange={handleEditUtilityChange}
                >
                  <option value="">Select type</option>
                  <option value="fixed">Fixed Amount</option>
                  <option value="varying">Variable (Based on Usage)</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                onClick={() => setIsEditUtilityModalOpen(false)}
                className="hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleEditUtilitySave}
                disabled={isSavingUtility}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-white transition-colors"
              >
                {isSavingUtility ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Alert Dialog */}
      {isAlertDialogOpen && (
        <div className="z-50 fixed inset-0 flex justify-center items-center bg-black/50 p-4">
          <div className="bg-white shadow-xl rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">System Alerts</h3>
                <button
                  onClick={() => setIsAlertDialogOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="mb-4 text-gray-500 text-sm">Recent alerts and notifications</p>

              <div className="space-y-4">
                <div className="flex items-start gap-3 bg-yellow-50 p-3 border border-yellow-200 rounded-lg">
                  <AlertTriangle className="mt-0.5 w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="font-medium text-sm">High Water Usage</p>
                    <p className="text-gray-500 text-xs">Green Apartments, APT 101 - 30% above average</p>
                    <p className="mt-1 text-gray-500 text-xs">2 hours ago</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-red-50 p-3 border border-red-200 rounded-lg">
                  <AlertTriangle className="mt-0.5 w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-sm">Overdue Payments</p>
                    <p className="text-gray-500 text-xs">5 tenants have overdue utility payments</p>
                    <p className="mt-1 text-gray-500 text-xs">1 day ago</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-blue-50 p-3 border border-blue-200 rounded-lg">
                  <Droplets className="mt-0.5 w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium text-sm">Water Meter Readings Due</p>
                    <p className="text-gray-500 text-xs">Readings due for 12 units by May 25, 2023</p>
                    <p className="mt-1 text-gray-500 text-xs">5 hours ago</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 bg-green-50 p-3 border border-green-200 rounded-lg">
                  <Zap className="mt-0.5 w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium text-sm">Electricity Usage Decreased</p>
                    <p className="text-gray-500 text-xs">Riverside Apts - 15% decrease from last month</p>
                    <p className="mt-1 text-gray-500 text-xs">1 day ago</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setIsAlertDialogOpen(false)}
                  className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-white"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
